"use client";

import { forwardRef } from "react";
import { cn } from "@/lib/utils";

interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> {
  /** Label text or element displayed next to the checkbox */
  label: React.ReactNode;
  /** Optional description text displayed below the label */
  description?: string;
  /** Show error styling (red border) */
  error?: boolean;
}

/**
 * Checkbox - Styled checkbox with label and optional description
 *
 * Features:
 * - Native checkbox input with custom styling via Tailwind
 * - Label rendered next to checkbox with text-body sizing
 * - Optional description below label in muted text
 * - 44px minimum touch target for accessibility
 * - Error state styling
 * - Disabled state with reduced opacity
 * - forwardRef for form library compatibility
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, description, error = false, className, disabled, id, ...props }, ref) => {
    const descriptionId = id && description ? `${id}-description` : undefined;

    return (
      <label
        htmlFor={id}
        className={cn(
          "flex items-start gap-3 min-h-[44px] cursor-pointer select-none",
          disabled && "opacity-50 cursor-not-allowed",
          className
        )}
      >
        <input
          ref={ref}
          id={id}
          type="checkbox"
          disabled={disabled}
          aria-invalid={error || undefined}
          aria-describedby={descriptionId}
          className={cn(
            "mt-0.5 w-5 h-5 rounded-md border-surface-dim accent-primary focus:ring-2 focus:ring-primary/20 cursor-pointer",
            error && "border-red-500",
            disabled && "cursor-not-allowed"
          )}
          {...props}
        />
        <div className="flex flex-col">
          <span className="text-body text-foreground">{label}</span>
          {description && (
            <span id={descriptionId} className="text-body-sm text-foreground-muted">
              {description}
            </span>
          )}
        </div>
      </label>
    );
  }
);

Checkbox.displayName = "Checkbox";
